import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { copyCouponIconSrc } from '../../assets/bazzar';
import {
  confettiCornerLeftSrc,
  confettiCornerRightSrc,
  rewardDetailCouponBoxSrc,
  rewardDetailPrizeCouponSrc,
} from '../../assets/my-rewards';
import { BAZAAR_DIALOG_INNER_BG, BAZAAR_DIALOG_INNER_SHADOW } from './bazaarDialogShared';
import { isUrlCouponReward, openRewardCouponInNewTab } from '../../services/bazaarService';
import { showAppError } from '../../services/messageDialogService';

const PANEL_WIDTH = 400;

/** Figma 11101:36975 — Congrats reward detail card */
export default function RapchikRewardDetailPanel({ reward }) {
  const [copied, setCopied] = useState(false);
  const couponCode = reward?.couponCode || '';
  const plainCode = couponCode.replace(/^#/, '');
  const imageUrl = reward?.imageUrl;
  const isUrlCoupon = isUrlCouponReward(reward);
  const canAccessCoupon = reward?.canAccessCoupon !== false && Boolean(couponCode);

  const winMessage = String(reward?.winMessage || '').trim();

  const handleCouponAction = async () => {
    if (!canAccessCoupon) {
      showAppError('Coupon is not available.');
      return;
    }

    if (isUrlCoupon) {
      openRewardCouponInNewTab(reward);
      return;
    }

    if (!plainCode) {
      showAppError('Coupon code is not available.');
      return;
    }

    try {
      await navigator.clipboard.writeText(plainCode);
      setCopied(true);
      toast.success('Coupon code copied!');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      showAppError('Unable to copy coupon code.');
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: PANEL_WIDTH, mx: 'auto' }}>
      <Box
        sx={{
          borderRadius: '36px',
          border: '1px solid #d6a7ff',
          background: 'linear-gradient(180deg, #8f51fb 0%, #5c1ec8 34.483%)',
          boxShadow: '0 0 16px 6px rgba(183, 89, 255, 0.35)',
          p: '5px',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: '32px',
            background: BAZAAR_DIALOG_INNER_BG,
            boxShadow: `${BAZAAR_DIALOG_INNER_SHADOW}, 0 4px 24px rgba(255, 0, 242, 0.2)`,
            minHeight: 334,
            overflow: 'hidden',
            px: 2,
            pt: 3,
            pb: 2.5,
          }}
        >
          <Box
            component="img"
            src={confettiCornerLeftSrc}
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 103,
              height: 104,
              pointerEvents: 'none',
            }}
          />
          <Box
            component="img"
            src={confettiCornerRightSrc}
            alt=""
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 103,
              height: 104,
              pointerEvents: 'none',
            }}
          />

          <Typography
            component="h2"
            sx={{
              position: 'relative',
              zIndex: 1,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: 37,
              lineHeight: '30px',
              textAlign: 'center',
              textTransform: 'uppercase',
              background: 'linear-gradient(270deg, #822aff 0%, #ff00f2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 4px 4px rgba(0, 0, 0, 0.1)',
              mb: 1.5,
            }}
          >
            Congrats!
          </Typography>

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              mb: 1.5,
              minHeight: 67,
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt=""
                sx={{ width: 93, height: 67, objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Box sx={{ position: 'relative', width: 93, height: 67, overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={rewardDetailPrizeCouponSrc}
                  alt=""
                  sx={{
                    position: 'absolute',
                    width: '112.28%',
                    height: '154.45%',
                    left: '-7.79%',
                    top: '-26.7%',
                    maxWidth: 'none',
                    display: 'block',
                  }}
                />
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              position: 'relative',
              zIndex: 1,
              color: '#fff',
              fontWeight: 600,
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.8px',
              textAlign: 'center',
              px: 1,
              mb: 2.5,
              whiteSpace: 'pre-line',
            }}
          >
            {winMessage || reward?.title}
          </Typography>

          <Box
            component="button"
            type="button"
            onClick={handleCouponAction}
            disabled={!canAccessCoupon}
            aria-label={isUrlCoupon ? 'Open coupon link in new tab' : 'Copy coupon code'}
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'block',
              width: '100%',
              maxWidth: 296,
              height: 78,
              mx: 'auto',
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              cursor: canAccessCoupon ? 'pointer' : 'not-allowed',
              opacity: canAccessCoupon ? 1 : 0.6,
            }}
          >
            <Box
              component="img"
              src={rewardDetailCouponBoxSrc}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'fill',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '88%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: '12px',
                  }}
                >
                  {isUrlCoupon ? 'Access Coupon' : copied ? 'Copied!' : 'Copy Coupon Code'}
                </Typography>
                <Box component="img" src={copyCouponIconSrc} alt="" sx={{ width: 16, height: 16, display: 'block' }} />
              </Box>
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: '32px',
                  letterSpacing: '0.8px',
                  textAlign: 'center',
                }}
              >
                {couponCode || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
